import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useContextSelector } from 'use-context-selector';

import {
  catalogMetricsContextWithSelector,
  catalogMetricsEmptyContextWithSelector,
  ICatalogMetricsContext,
} from './CatalogMetrics.type';

const useCatalogMetricsContextValueSelector = <
  TMetrics extends { currency: string | null },
  T,
>(
  selector: (context: ICatalogMetricsContext<TMetrics>) => T
): T => {
  const isConsignorMasterView = useUserHasConsignorMasterUI();

  const contextValue = useContextSelector(
    isConsignorMasterView
      ? catalogMetricsEmptyContextWithSelector<TMetrics>()
      : catalogMetricsContextWithSelector<TMetrics>(),
    selector
  );

  return contextValue;
};

export const useCatalogMetricsContextEventDetailedMetricsSelector = <
  TMetrics extends { currency: string | null },
>(): Record<string, TMetrics> | undefined => {
  return useCatalogMetricsContextValueSelector<
    TMetrics,
    Record<string, TMetrics> | undefined
  >((context) => context.eventDetailedMetrics);
};

export const useCatalogMetricsContextEventMetricsSelector = <
  TMetrics extends { currency: string | null },
>(): Record<string, TMetrics> | undefined => {
  return useCatalogMetricsContextValueSelector<
    TMetrics,
    Record<string, TMetrics> | undefined
  >((context) => context.eventMetrics);
};

export const useCatalogMetricsContextIsLoadingSelector = <
  TMetrics extends { currency: string | null },
>(): boolean => {
  return useCatalogMetricsContextValueSelector<TMetrics, boolean>(
    (context) => context.isLoading
  );
};

export const useCatalogMetricsContextRefreshMetricsSelector = <
  TMetrics extends { currency: string | null },
>(): (() => void) => {
  return useCatalogMetricsContextValueSelector<TMetrics, () => void>(
    (context) => context.refreshMetrics
  );
};
